import each from 'lodash/each';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import reject from 'lodash/reject';
import find from 'lodash/find';
import noop from 'lodash/noop';

import { get as getPageHistoryTracker } from 'zoomforth_zf/appglobals/page-history-tracker.js';

import {
  showModal
} from './editor_modals.js';

import { sendRequestWithPromise } from 'zoomforth_zfutils/xhr_tools.js';
import { showNotificationErrorForXhr } from 'zoomforth_tools/xhr_error_notifications.js';
import { structureThemeRestData } from 'zoomforth_codesbane_editor/lib/themes.js';

import { setSidebarIsExpanded } from './misc.js';
import { ensureThrottledPageConfigSaving } from './config.js';

import {
  setStylePreset
} from 'zoomforth_codesbane_editor/action_creators/sections.grid.js';

import {
  getCompatibleThemes,
  getContentItemsForStylePresetMapping
} from 'zoomforth_codesbane_editor/reducers/index.js';

import {
  getStylePresets,
  getPageThemeUuid
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  SET_IS_THEME_SELECTOR_OPEN,
  SET_IS_STYLE_SELECTOR_OPEN,
  SET_IS_PAGE_SCOPED_CSS_EDITOR_OPEN,
  SET_IS_SHOWING_COMPATIBLE_THEMES
} from 'zoomforth_codesbane_editor/action_types.js';

import MapStylePresetsModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/map_style_presets_modal_container.jsx';

import ConfirmSelectIncompatibleThemeModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_select_incompatible_theme_modal_container.jsx';

export const fetchThemes = ({ onSuccess = noop, onError = noop } = {})=>
  (dispatch, getState)=> {
    dispatch({
      type: 'SET_LOADING_THEMES',
      value: true
    });

    sendRequestWithPromise({
      url: '/api/page_theme_summaries',
      method: 'GET'
    })
      .then((themes)=> {
        const currentThemeUuid = getPageThemeUuid(getState());

        // sort available themes based on the current theme selected for the page.
        // this allows the top theme to always be the selected one when you click themes,
        // but does not force immediate re-sorting if a different one is selected.
        const sortedThemes = sortBy(themes, ({uuid, id}) => {
          return uuid === currentThemeUuid ? 0 : id;
        });

        // Filter themes that should not be available such as hidden themes.
        // The only exception is if the page currently uses a hidden theme.
        const filteredThemes = filter(sortedThemes, ({ uuid, is_hidden })=> {
          return currentThemeUuid === uuid || !is_hidden;
        });

        dispatch({
          type: 'SET_THEMES',
          themes: filteredThemes
        });

        onSuccess();
      })
      .catch((xhr)=> {
        dispatch(showNotificationErrorForXhr(xhr));
        onError();
      })
      .finally(()=> {
        dispatch({
          type: 'SET_LOADING_THEMES',
          value: false
        });
      });
  };

export const openThemeSelector = ()=>
  (dispatch)=> {
    // Give the sidebar some breathing room
    dispatch(setSidebarIsExpanded(true));

    dispatch({
      type: SET_IS_THEME_SELECTOR_OPEN,
      isOpen: true
    });
  };


export const closeThemeSelector = ()=>
  (dispatch)=> {
    // Set the sidebar back to normal
    dispatch(setSidebarIsExpanded(false));

    dispatch({
      type: SET_IS_THEME_SELECTOR_OPEN,
      isOpen: false
    });
  };


export const openStyleSelector = ()=>
  (dispatch)=> {
    // we fetch themes every time in case a user was working on a new theme
    // or making edits which will affect the current page.
    dispatch(fetchThemes());

    dispatch({
      type: SET_IS_STYLE_SELECTOR_OPEN,
      isOpen: true
    });
  };


export const closeStyleSelector = ()=> {
  return {
    type: SET_IS_STYLE_SELECTOR_OPEN,
    isOpen: false
  };
};


export const openPageScopedCssEditor = ()=>
  (dispatch)=> {
    dispatch({
      type: SET_IS_PAGE_SCOPED_CSS_EDITOR_OPEN,
      isOpen: true
    });
  };


export const closePageScopedCssEditor = ()=> {
  return {
    type: SET_IS_PAGE_SCOPED_CSS_EDITOR_OPEN,
    isOpen: false
  };
};


export const setIsMobileRenderEnabled = (value)=>
  (dispatch)=> {
    dispatch({
      type: 'SET_IS_MOBILE_RENDER_ENABLED',
      value
    });
    dispatch(ensureThrottledPageConfigSaving());
  };


export const fetchStylePresetMappingMeta = (themeUuid, { onSuccess = noop } = {})=>
  (dispatch, getState)=> {
    sendRequestWithPromise({
      method: 'GET',
      url: `/api/page_themes/${themeUuid}`
    })
      .then((pageTheme)=> {
        const newThemeData = structureThemeRestData(pageTheme);
        const oldThemeStylePresets = reject(getStylePresets(getState()), ['className', '']);
        const compatibleThemes = getCompatibleThemes(getState());
        const isNewThemeCompatible = !!find(compatibleThemes, ['uuid', pageTheme.uuid]);
        const isMappingStylePresetsRequired = !(oldThemeStylePresets.length === 0 || newThemeData.stylePresets.length === 0);

        onSuccess({
          newThemeData,
          oldThemeStylePresets,
          isNewThemeCompatible,
          isMappingStylePresetsRequired
        });
      });
  };


export const openStylePresetMapperOrSetTheme = (themeUuid)=>
  (dispatch)=> {
    dispatch(fetchStylePresetMappingMeta(themeUuid, { onSuccess: ({
      newThemeData,
      oldThemeStylePresets,
      isNewThemeCompatible,
      isMappingStylePresetsRequired
    })=> {
      if (isNewThemeCompatible) {
        if (isMappingStylePresetsRequired) {
          dispatch(showModal(MapStylePresetsModal, { newThemeData }));
        } else {
          dispatch(setCurrentTheme(newThemeData));
        }
      } else {
        dispatch(showModal(ConfirmSelectIncompatibleThemeModal, {
          newThemeData,
          oldThemeStylePresets,
          isNewThemeCompatible,
          isMappingStylePresetsRequired
        }));
      }
    } }));
  };


export const setCurrentTheme = (newThemeData, stylePresetMapping={})=>
  (dispatch, getState)=> {
    getPageHistoryTracker().trackChangeTheme(
      newThemeData.id,
      newThemeData.name || 'Untitled Theme'
    );

    const contentItemsToChange = getContentItemsForStylePresetMapping(
      getState(),
      stylePresetMapping
    );

    each(contentItemsToChange, (contentItem)=> {
      dispatch(setStylePreset(
        contentItem,
        stylePresetMapping[contentItem.style_preset_classname]
      ));
    });

    dispatch({
      type: 'SET_CURRENT_THEME',
      ...newThemeData
    });

    dispatch(ensureThrottledPageConfigSaving());
  };


export const setPageScopedCss = (pageScopedCss)=> {
  return {
    type: 'SET_PAGE_SCOPED_CSS',
    pageScopedCss
  };
};


export const setIsPageScopedCssValid = (isValid)=> {
  return {
    type: 'SET_IS_PAGE_SCOPED_CSS_VALID',
    isValid
  };
};

export const setIsShowingCompatibleThemes = (isShowingCompatibleThemes)=> {
  return { type: SET_IS_SHOWING_COMPATIBLE_THEMES, isShowingCompatibleThemes };
};
